/// <reference types="applepayjs" />
'use client';

import { API_ROUTES } from '@shared/api-utils';
import { useDebug } from '@shared/debug';
import { useScript } from '@shared/hooks';
import { useFullStoryTrackEvent } from '@shared/tracking';
import type { Currency } from '@shared/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApplePaySession } from './useApplePaySession';

/**
 * How far an Apple Pay session got — reported on cancellation to tell a user dismissing the sheet
 * apart from the system cancelling it mid-payment.
 */
const APPLE_PAY_STAGE = {
  VALIDATING_MERCHANT: 'validating_merchant',
  VALIDATION_FAILED: 'validation_failed',
  MERCHANT_VALIDATED: 'merchant_validated',
  AUTHORISING: 'authorising',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const;

type ApplePayStage = (typeof APPLE_PAY_STAGE)[keyof typeof APPLE_PAY_STAGE];

type ApplePayPaymentRequestResult = {
  success: boolean;
  data?: ApplePayJS.ApplePayPaymentRequest;
  reason?: string;
};

type UseApplePayOptions = {
  amount: string;
  currency: Currency;
  playerCountry: string;
  merchantLabel: string;
  lottolandButtonId?: string;
  onError?: (error: string) => void;
  onPaymentAuthorized?: (paymentData: {
    data: string;
    signature: string;
    transactionId: string;
    ephemeralPublicKey: string;
    publicKeyHash: string;
    version: string;
  }) => Promise<boolean> | void;
};

type UseApplePayReturn = {
  isApplePayEnabled: boolean;
  errorMessage: string | null;
  isLoading: boolean;
  handleClick: () => Promise<void>;
  getData: () => Promise<string>;
  setData: (data: { data: string; signature: string; transactionId: string; ephemeralPublicKey: string; publicKeyHash: string; version: string }) => void;
  initializeApplePay: () => Promise<void>;
};

// Browser API wrappers for testability
const browserAPIs = {
  hasApplePaySession: (): boolean => typeof window !== 'undefined' && 'ApplePaySession' in window,
  canMakePayments: (): boolean => {
    if (typeof window === 'undefined' || !('ApplePaySession' in window)) return false;
    const ApplePaySessionClass = (window as { ApplePaySession: { canMakePayments: () => boolean } }).ApplePaySession;
    return ApplePaySessionClass.canMakePayments();
  },
  querySelector: (selector: string): Element | null => document.querySelector(selector),
  postMessage: (message: { type: string; height: number }, targetOrigin: string): void => {
    window.top?.postMessage(message, targetOrigin);
  },
  createMutationObserver: (callback: MutationCallback): MutationObserver => new MutationObserver(callback),
  btoa: (str: string): string => btoa(str),
};

function useApplePay({ amount, currency, playerCountry, merchantLabel, onError, onPaymentAuthorized }: UseApplePayOptions): UseApplePayReturn {
  const debug = useDebug();
  const { addScriptTag } = useScript();
  const applePaySession = useApplePaySession();
  const fullStoryTrackEvent = useFullStoryTrackEvent();

  const [isApplePayEnabled, setIsApplePayEnabled] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const paymentDataRef = useRef<{
    data: string;
    signature: string;
    transactionId: string;
    ephemeralPublicKey: string;
    publicKeyHash: string;
    version: string;
  } | null>(null);

  const stageRef = useRef<ApplePayStage | null>(null);
  const mutationObserverRef = useRef<MutationObserver | null>(null);
  const documentObserverRef = useRef<MutationObserver | null>(null);

  const handleError = useCallback(
    (error: unknown) => {
      let reason: string | undefined;

      if (error instanceof Error) {
        reason = error.message;
      } else if (typeof error === 'string') {
        reason = error;
      }

      debug.forceError('Apple Pay error:', error);

      if (reason) {
        debug.forceError('Apple Pay error reason:', reason);
      }

      setErrorMessage(reason || 'Apple Pay error occurred');
      onError?.(reason || 'Apple Pay error occurred');
    },
    [onError, debug]
  );

  const addApplePaySdkScript = useCallback(async () => {
    try {
      await addScriptTag(API_ROUTES.PAYMENTS.APPLE_PAY_SDK_LIBRARY_SRC);
    } catch (error) {
      debug.forceError('Error loading Apple Pay SDK library');
      throw new Error((error as Error).message || (error as string), { cause: error });
    }
  }, [addScriptTag, debug]);

  const checkApplePay = useCallback(() => {
    if (browserAPIs.hasApplePaySession()) {
      setIsApplePayEnabled(browserAPIs.canMakePayments());
    } else {
      setIsApplePayEnabled(false);
    }
  }, []);

  const handleApplePaymentRequest = useCallback(async (): Promise<ApplePayPaymentRequestResult> => {
    debug.log('Creating payment request with:', { playerCountry, currency, merchantLabel, amount });
    const paymentRequestResult = applePaySession.createApplePayPaymentRequest(playerCountry, currency, merchantLabel, 'final', amount);
    debug.log('Payment request created:', paymentRequestResult.data);
    return { success: true, data: paymentRequestResult.data };
  }, [applePaySession, playerCountry, currency, merchantLabel, amount, debug]);

  const handleApplePaySessionCreation = useCallback(
    async (data: ApplePayJS.ApplePayPaymentRequest) => {
      const appleSessionResult = applePaySession.createApplePaySession(data);
      if (!appleSessionResult.success) {
        return { success: false, reason: 'Unable to get Apple Pay session data' };
      }
      return { success: true, data: appleSessionResult.data };
    },
    [applePaySession]
  );

  const validateSessionCreationResult = useCallback(
    (sessionCreationResult: { success: boolean; data?: ApplePaySession | undefined; reason?: string | undefined }) => {
      if (!sessionCreationResult.success || !sessionCreationResult.data) {
        debug.forceError('Unable to get Apple Pay session data');
        throw new Error(sessionCreationResult.reason ?? 'Unable to get Apple Pay session data');
      }
    },
    [debug]
  );

  const createSession = useCallback(
    async (paymentRequestResult: ApplePayPaymentRequestResult) => {
      if (paymentRequestResult.data === undefined) {
        debug.forceError('Unable to get Apple Pay session data');
        throw new Error(paymentRequestResult.reason ?? 'Unable to get Apple Pay session data');
      }

      const sessionCreationResult = await handleApplePaySessionCreation(paymentRequestResult.data);
      if (!sessionCreationResult.success) {
        debug.forceError('Unable to get Apple Pay session data');
        throw new Error(paymentRequestResult.reason ?? 'Unable to get Apple Pay session data');
      }

      validateSessionCreationResult(sessionCreationResult);
      return sessionCreationResult.data;
    },
    [handleApplePaySessionCreation, validateSessionCreationResult, debug]
  );

  const checkApplePayRequestData = useCallback(
    (paymentRequestResult: ApplePayPaymentRequestResult) => {
      if (!paymentRequestResult.success) {
        debug.forceError('Unable to get Apple Pay session data');
        throw new Error(paymentRequestResult.reason);
      }
      if (!paymentRequestResult.data) {
        debug.forceError('Unable to create Apple Pay payment request data');
        throw new Error('Unable to create Apple Pay payment request data');
      }
      return paymentRequestResult;
    },
    [debug]
  );

  const finalizePayment = useCallback(
    (session: ApplePaySession): ApplePaySession => {
      debug.log('Setting up onpaymentauthorized handler');
      session.onpaymentauthorized = async (event: ApplePayJS.ApplePayPaymentAuthorizedEvent) => {
        debug.log('onpaymentauthorized FIRED - event received');
        stageRef.current = APPLE_PAY_STAGE.AUTHORISING;
        try {
          const paymentData = {
            data: event.payment.token.paymentData.data,
            signature: event.payment.token.paymentData.signature,
            transactionId: event.payment.token.paymentData.header.transactionId,
            ephemeralPublicKey: event.payment.token.paymentData.header.ephemeralPublicKey,
            publicKeyHash: event.payment.token.paymentData.header.publicKeyHash,
            version: event.payment.token.paymentData.version,
          };

          paymentDataRef.current = paymentData;
          debug.log('Payment authorized, processing with backend...');
          debug.log('onPaymentAuthorized callback exists?', !!onPaymentAuthorized);

          // Call the backend to process the payment and wait for result
          const result = await onPaymentAuthorized?.(paymentData);

          debug.log('Backend processing result:', result);
          debug.log('Result type:', typeof result);

          // Complete payment based on backend result
          if (result === false) {
            debug.forceError('Payment processing failed, completing with failure status');
            session.completePayment({
              status: ApplePaySession.STATUS_FAILURE,
            });
            stageRef.current = APPLE_PAY_STAGE.FAILED;
          } else {
            debug.log('Payment processing successful, completing with success status');
            session.completePayment({
              status: ApplePaySession.STATUS_SUCCESS,
            });
            stageRef.current = APPLE_PAY_STAGE.COMPLETED;
          }
        } catch (e) {
          debug.forceError('Error during payment authorization:', e);
          stageRef.current = APPLE_PAY_STAGE.FAILED;
          session.completePayment({
            status: ApplePaySession.STATUS_FAILURE,
          });
          handleError(e);
        }
      };
      debug.log('onpaymentauthorized handler registered');
      return session;
    },
    [handleError, onPaymentAuthorized, debug]
  );

  const paymentMethodSelected = useCallback(
    (session: ApplePaySession): ApplePaySession => {
      debug.log('Setting up onpaymentmethodselected handler');
      session.onpaymentmethodselected = () => {
        debug.log('onpaymentmethodselected FIRED');
        try {
          const newTotal: ApplePayJS.ApplePayLineItem = { amount, label: merchantLabel };
          const lineItems: ApplePayJS.ApplePayLineItem[] = [{ amount, label: merchantLabel }];

          session.completePaymentMethodSelection(newTotal, lineItems);
          debug.log('Payment method selection completed successfully');
        } catch (e) {
          debug.forceError('Unable to complete payment selection', e);
          handleError(e);
        }
      };
      debug.log('onpaymentmethodselected handler registered');
      return session;
    },
    [amount, merchantLabel, handleError, debug]
  );

  const endSession = useCallback(
    (session: ApplePaySession | undefined) => {
      try {
        if (!session) {
          debug.forceError('Unable to end Apple session properly, session is undefined');
          throw new Error('Unable to end Apple session properly, session is undefined');
        }
        applePaySession.invalidateSession();
      } catch {
        handleError(new Error(`Error aborting Apple Pay session.`));
      }
    },
    [applePaySession, handleError, debug]
  );

  const getSignature = useCallback(
    (merchantSessionResult: { success: boolean; data?: { signature?: string } }) => {
      if (!merchantSessionResult.success || !merchantSessionResult.data?.signature) {
        debug.forceError('Unable to get merchant session data');
        throw new Error('Unable to get merchant session data');
      }
      return merchantSessionResult.data.signature;
    },
    [debug]
  );

  const validateMerchant = useCallback(
    (session: ApplePaySession): ApplePaySession => {
      debug.log('Setting up onvalidatemerchant handler');
      session.onvalidatemerchant = async (event: ApplePayJS.ApplePayValidateMerchantEvent) => {
        debug.log('onvalidatemerchant FIRED - validation URL:', event.validationURL);
        try {
          debug.log('event.validationURL', event.validationURL);

          const merchantSessionResult = await applePaySession.getMerchantSession(event.validationURL);

          debug.log('merchantSessionResult', merchantSessionResult);
          if (!merchantSessionResult.success) {
            debug.forceError('Unable to complete merchant validation');
            throw new Error('Unable to complete merchant validation');
          }

          getSignature(merchantSessionResult);

          debug.log('merchantSessionResult.data', merchantSessionResult.data);

          // Call completeMerchantValidation directly on the session object
          // instead of going through the hook (which relies on state that may not be updated yet)
          try {
            debug.log('About to call session.completeMerchantValidation...');
            session.completeMerchantValidation(merchantSessionResult.data);
            stageRef.current = APPLE_PAY_STAGE.MERCHANT_VALIDATED;
            debug.log('Merchant validation completed successfully - Apple Pay sheet should now be interactive');
          } catch (error) {
            debug.forceError('completeMerchantValidation failed with error:', error);
            throw new Error('Unable to complete merchant validation', { cause: error });
          }
        } catch (e) {
          debug.forceError('Error in onvalidatemerchant:', e);
          stageRef.current = APPLE_PAY_STAGE.VALIDATION_FAILED;
          // Close the sheet now rather than leaving it spinning until Apple times the session out.
          try {
            session.abort();
          } catch {
            // The session may already have been cancelled or timed out.
          }
          handleError(e);
        }
      };
      debug.log('onvalidatemerchant handler registered');
      return session;
    },
    [applePaySession, handleError, getSignature, debug]
  );

  const fixPointerEvents = useCallback((applePayModalElement: Element | null) => {
    if (applePayModalElement) {
      (applePayModalElement as HTMLElement).style.pointerEvents = 'auto';
    }
  }, []);

  const resizeIframe = useCallback(() => {
    const applePayModalElement = browserAPIs.querySelector('apple-pay-modal');
    const MINIMUM_POSSIBLE_SIZE = 660;

    fixPointerEvents(applePayModalElement);

    mutationObserverRef.current?.disconnect();
    documentObserverRef.current?.disconnect();

    mutationObserverRef.current = browserAPIs.createMutationObserver(() => {
      const currentModalElement = browserAPIs.querySelector('apple-pay-modal');
      fixPointerEvents(currentModalElement);
      if (currentModalElement?.shadowRoot?.querySelector('.modal')?.classList.contains('modal-open')) {
        browserAPIs.postMessage({ type: 'size', height: MINIMUM_POSSIBLE_SIZE }, '*');
        mutationObserverRef.current?.disconnect();
      }
    });

    if (applePayModalElement?.shadowRoot) {
      mutationObserverRef.current.observe(applePayModalElement.shadowRoot, { childList: true, subtree: true });
    } else {
      documentObserverRef.current = browserAPIs.createMutationObserver(() => {
        const modalElement = browserAPIs.querySelector('apple-pay-modal');
        if (modalElement?.shadowRoot) {
          fixPointerEvents(modalElement);
          mutationObserverRef.current?.observe(modalElement.shadowRoot, { childList: true, subtree: true });
          documentObserverRef.current?.disconnect();
          documentObserverRef.current = null;
        }
      });
      documentObserverRef.current.observe(document.body, { childList: true, subtree: true });
    }
  }, [fixPointerEvents]);

  const handleSession = useCallback(
    (session: ApplePaySession | undefined) => {
      debug.log('handleSession called, session exists:', !!session);

      if (!session) {
        debug.forceError('Unable to complete merchant validation');
        throw new Error('Unable to complete merchant validation');
      }

      debug.log('Configuring session handlers...');
      const configuredSession = finalizePayment(paymentMethodSelected(validateMerchant(session)));
      configuredSession.oncancel = () => {
        const stage = stageRef.current;
        if (stage === APPLE_PAY_STAGE.AUTHORISING) {
          debug.forceError('Apple Pay session cancelled during payment authorisation', { stage });
        } else {
          debug.log('Apple Pay session cancelled', { stage });
        }
        fullStoryTrackEvent('Deposit | Apple Pay Cancelled', { stage, currency, amount: Number(amount) });
        endSession(session);
      };

      // Verify all handlers are set
      debug.log('Session handlers configured:', {
        onvalidatemerchant: !!configuredSession.onvalidatemerchant,
        onpaymentmethodselected: !!configuredSession.onpaymentmethodselected,
        onpaymentauthorized: !!configuredSession.onpaymentauthorized,
        oncancel: !!configuredSession.oncancel,
      });

      resizeIframe();

      stageRef.current = APPLE_PAY_STAGE.VALIDATING_MERCHANT;
      try {
        debug.log('Calling session.begin()...');
        configuredSession.begin();
        debug.log('session.begin() completed successfully');
      } catch (error) {
        debug.forceError('Error beginning Apple Pay session:', error);
        throw new Error(`Error beginning Apple Pay session`, { cause: error });
      }

      return configuredSession;
    },
    [finalizePayment, paymentMethodSelected, validateMerchant, endSession, resizeIframe, fullStoryTrackEvent, currency, amount, debug]
  );

  const initializeApplePay = useCallback(async () => {
    try {
      await addApplePaySdkScript();
      checkApplePay();
    } catch (e) {
      handleError(e);
    }
  }, [addApplePaySdkScript, checkApplePay, handleError]);

  const handleClick = useCallback(async () => {
    debug.log('Apple Pay button clicked - starting payment flow');
    setErrorMessage(null);
    setIsLoading(true);
    try {
      debug.log('Initializing Apple Pay...');
      await initializeApplePay();

      debug.log('Creating payment request...');
      const paymentRequestResult = await handleApplePaymentRequest();
      const paymentRequestData = checkApplePayRequestData(paymentRequestResult);

      debug.log('Creating Apple Pay session...');
      const session = await createSession(paymentRequestData);
      debug.log('Session created, configuring handlers...');

      handleSession(session);
      debug.log('Payment flow initiated successfully');
    } catch (e) {
      debug.forceError('Error in handleClick:', e);
      handleError(e);
    } finally {
      setIsLoading(false);
    }
  }, [initializeApplePay, handleApplePaymentRequest, checkApplePayRequestData, createSession, handleSession, handleError, debug]);

  const setData = useCallback((data: { data: string; signature: string; transactionId: string; ephemeralPublicKey: string; publicKeyHash: string; version: string }) => {
    paymentDataRef.current = data;
  }, []);

  const getData = useCallback(async (): Promise<string> => {
    if (!paymentDataRef.current) {
      debug.forceError('No payment data available');
      return '';
    }

    const payload = paymentDataRef.current;
    return browserAPIs.btoa(JSON.stringify(payload));
  }, [debug]);

  // Cleanup mutation observer on unmount
  useEffect(() => {
    return () => {
      mutationObserverRef.current?.disconnect();
      documentObserverRef.current?.disconnect();
    };
  }, []);

  return {
    isApplePayEnabled,
    errorMessage,
    isLoading,
    handleClick,
    getData,
    setData,
    initializeApplePay,
  };
}

export { APPLE_PAY_STAGE, browserAPIs, useApplePay, type ApplePayPaymentRequestResult, type ApplePayStage, type UseApplePayOptions, type UseApplePayReturn };
